/**
 * Joy-UI-inspired display primitives for portal widgets (ADR-0010 S17).
 *
 * A small, shared visual vocabulary — soft status `Chip`s (colour + icon),
 * initial `Avatar`s, and a smart table-cell renderer — so every data widget
 * (and the new components in #6) reads like a polished Joy UI dashboard rather
 * than a raw HTML table. Pure presentational; no app state.
 */
import { Check, Circle, Clock, RefreshCw, XCircle } from "lucide-react";
import type { ReactNode } from "react";

export type ChipColor = "neutral" | "primary" | "success" | "warning" | "danger";

const CHIP_SOFT: Record<ChipColor, string> = {
  neutral: "bg-muted/60 text-muted-foreground",
  primary: "bg-accent/10 text-accent",
  success: "bg-success/12 text-success",
  warning: "bg-warning/15 text-warning",
  danger: "bg-danger/10 text-danger",
};

/** A soft, pill-shaped status badge (Joy "soft" variant). */
export function Chip({
  color = "neutral",
  icon,
  children,
}: {
  color?: ChipColor;
  icon?: ReactNode;
  children: ReactNode;
}): ReactNode {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${CHIP_SOFT[color]}`}
    >
      {icon}
      {children}
    </span>
  );
}

// A curated status-word → (colour, icon) map. Only RECOGNISED words become a
// chip (case-insensitive); anything else renders as plain text — so this never
// mangles arbitrary data, it just lights up the common lifecycle states the
// way a Joy UI dashboard does (paid ✓, cancelled ⊘, refunded ↻…).
const ICON = "h-3 w-3";
const STATUS: Record<string, { color: ChipColor; icon: ReactNode }> = {
  paid: { color: "success", icon: <Check className={ICON} /> },
  active: { color: "success", icon: <Check className={ICON} /> },
  done: { color: "success", icon: <Check className={ICON} /> },
  complete: { color: "success", icon: <Check className={ICON} /> },
  completed: { color: "success", icon: <Check className={ICON} /> },
  success: { color: "success", icon: <Check className={ICON} /> },
  approved: { color: "success", icon: <Check className={ICON} /> },
  open: { color: "primary", icon: <Circle className={ICON} /> },
  cancelled: { color: "danger", icon: <XCircle className={ICON} /> },
  canceled: { color: "danger", icon: <XCircle className={ICON} /> },
  failed: { color: "danger", icon: <XCircle className={ICON} /> },
  error: { color: "danger", icon: <XCircle className={ICON} /> },
  rejected: { color: "danger", icon: <XCircle className={ICON} /> },
  refunded: { color: "neutral", icon: <RefreshCw className={ICON} /> },
  pending: { color: "warning", icon: <Clock className={ICON} /> },
  processing: { color: "warning", icon: <RefreshCw className={ICON} /> },
  draft: { color: "neutral", icon: <Circle className={ICON} /> },
};

/** A status chip for a recognised lifecycle word, else `null`. */
export function statusChip(value: string): ReactNode | null {
  const s = STATUS[value.trim().toLowerCase()];
  return s ? (
    <Chip color={s.color} icon={s.icon}>
      {value}
    </Chip>
  ) : null;
}

const AVATAR_TINTS = [
  "bg-accent/15 text-accent",
  "bg-success/15 text-success",
  "bg-warning/20 text-warning",
  "bg-danger/15 text-danger",
  "bg-info/15 text-info",
];

/** A circular initials avatar with a deterministic soft tint from the name. */
export function Avatar({ name }: { name: string }): ReactNode {
  const initials =
    name
      .trim()
      .split(/\s+/)
      .map((w) => w[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?";
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  const tint = AVATAR_TINTS[h % AVATAR_TINTS.length];
  return (
    <span
      className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold ${tint}`}
      aria-hidden
    >
      {initials}
    </span>
  );
}

/** Plain-string form of a cell value (for `title=` tooltips + search). */
export function cellText(value: unknown): string {
  if (value == null) return "—";
  // A `multiselect` cell is a JSON array of choices → a readable joined string.
  if (Array.isArray(value)) return value.map((v) => String(v)).join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

/** Smart table-cell content (S17): booleans → Yes/No chip, recognised status
 *  words → soft status chip, null → muted dash, else plain text. */
export function renderCellValue(value: unknown): ReactNode {
  if (value == null || value === "") return <span className="text-muted-foreground/70">—</span>;
  // A `multiselect` cell is a JSON array → one soft chip per value.
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-muted-foreground/70">—</span>;
    return (
      <span className="flex flex-wrap gap-1">
        {value.map((v, i) => (
          <Chip key={i} color="neutral">
            {String(v)}
          </Chip>
        ))}
      </span>
    );
  }
  if (typeof value === "boolean")
    return (
      <Chip color={value ? "success" : "neutral"} icon={value ? <Check className={ICON} /> : undefined}>
        {value ? "Yes" : "No"}
      </Chip>
    );
  if (typeof value === "object") return <span className="font-mono text-xs">{JSON.stringify(value)}</span>;
  const s = String(value);
  return statusChip(s) ?? s;
}
